export const NWC_PLACEHOLDER_IMAGE = 'assets/images/nwc-logo.webp';
export const PROJECT_PLACEHOLDER_IMAGE = 'assets/images/project_placeholder.png';
export const VIEWER_360_PLACEHOLDER_IMAGE = 'assets/krpano/media/space.png';
export const NWC_VIEWER_360_PLACEHOLDER_IMAGE = 'assets/krpano/media/nwc-space.png';
export const VIEWER_WATERMARK_LOGO = 'assets/images/logo/logo-text-on-dark.svg';
export const HEADER_LOGO_FULL = 'assets/images/logo/logo-text.svg';
export const HEADER_LOGO_ICON = 'assets/images/logo/logo.svg';

const OBSERVATION_PLACEHOLDER_PREFIX = 'assets/images/no_image_placeholder_';
const SUPPORTED_PLACEHOLDER_LANGS = new Set(['ar', 'en', 'ru']);

const WHITEHELMET_DEFAULT_IMAGE_MARKERS = [
  'assets/images/bg-01.jpg',
  'assets/images/project_placeholder.png',
  'assets/images/placeholder.png',
];

function normalizeImageUrl(imageUrl: string): string {
  const lowerCaseUrl = imageUrl.trim().toLowerCase();

  try {
    return decodeURIComponent(lowerCaseUrl);
  } catch {
    return lowerCaseUrl;
  }
}

function normalizeImagePath(imageUrl: string): string {
  const normalizedUrl = normalizeImageUrl(imageUrl);

  try {
    return new URL(normalizedUrl, 'https://placeholder.local').pathname.replace(/^\/+/, '');
  } catch {
    return normalizedUrl.split(/[?#]/)[0].replace(/^\/+/, '');
  }
}

export function isWhiteHelmetDefaultImage(imageUrl?: string | null): boolean {
  if (!imageUrl) {
    return false;
  }

  const normalizedPath = normalizeImagePath(imageUrl);
  return (
    WHITEHELMET_DEFAULT_IMAGE_MARKERS.includes(normalizedPath) ||
    normalizedPath.startsWith('assets/images/no_image_placeholder_')
  );
}

export function getProjectPlaceholderImage(
  imageUrl: string | null | undefined,
  isNwcClient: boolean,
): string | null | undefined {
  if (isNwcClient && (!imageUrl || isWhiteHelmetDefaultImage(imageUrl))) {
    return NWC_PLACEHOLDER_IMAGE;
  }

  return imageUrl;
}

export function getObservationPlaceholderImage(
  lang: string | null | undefined,
  isNwcClient: boolean,
): string {
  if (isNwcClient) {
    return NWC_PLACEHOLDER_IMAGE;
  }

  const normalizedLang = (lang || 'en').split('-')[0].toLowerCase();
  const placeholderLang = SUPPORTED_PLACEHOLDER_LANGS.has(normalizedLang) ? normalizedLang : 'en';

  return `${OBSERVATION_PLACEHOLDER_PREFIX}${placeholderLang}.png`;
}

export function getUploadPlaceholderImage(isNwcClient: boolean): string {
  return isNwcClient ? NWC_PLACEHOLDER_IMAGE : PROJECT_PLACEHOLDER_IMAGE;
}

export function getEmptyStatePlaceholderImage(isNwcClient: boolean): string | null {
  return isNwcClient ? NWC_PLACEHOLDER_IMAGE : null;
}

export function getLoadingPlaceholderLogo(isNwcClient: boolean): string | null {
  return isNwcClient ? NWC_PLACEHOLDER_IMAGE : null;
}

export function isViewer360PlaceholderImage(imageUrl?: string | null): boolean {
  if (!imageUrl) {
    return false;
  }

  const normalizedPath = normalizeImagePath(imageUrl);
  return (
    normalizedPath === VIEWER_360_PLACEHOLDER_IMAGE ||
    normalizedPath === NWC_VIEWER_360_PLACEHOLDER_IMAGE
  );
}

export function getViewer360Image(
  imageUrl: string | null | undefined,
  isNwcClient: boolean,
): string | null | undefined {
  if (isNwcClient && isViewer360PlaceholderImage(imageUrl)) {
    return NWC_VIEWER_360_PLACEHOLDER_IMAGE;
  }

  return imageUrl;
}

/**
 * Corner watermark logo shown on top of the 360 Viewer / Time Lapse panorama.
 * Returns the NWC logo for NWC projects instead of the default WhiteHelmet mark.
 */
export function getViewerWatermarkLogo(isNwcClient: boolean): string {
  return isNwcClient ? NWC_PLACEHOLDER_IMAGE : VIEWER_WATERMARK_LOGO;
}

/**
 * Main app header/sidebar logo shown in light mode. Unlike the dark-mode
 * counterpart (bound to PersonalizationService.logoFull/logoIcon), the
 * light-mode logo markup was hardcoded to the WhiteHelmet asset, so NWC
 * users kept seeing it regardless of their organization branding.
 */
export function getHeaderLogoFull(isNwcClient: boolean): string {
  return isNwcClient ? NWC_PLACEHOLDER_IMAGE : HEADER_LOGO_FULL;
}

export function getHeaderLogoIcon(isNwcClient: boolean): string {
  return isNwcClient ? NWC_PLACEHOLDER_IMAGE : HEADER_LOGO_ICON;
}
